import React, { useState, useEffect, useCallback } from 'react';
import { Users, Crown, Loader, UserPlus, RefreshCw, UserMinus, Check, X, Clock, Bell } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../services/apiUtil';
import ConfirmModal from '../../components/ConfirmModal';
import ProfileAvatar from '../../profile/components/ProfileAvatar';
import toast from 'react-hot-toast';
import { btn } from '../../utils/buttonStyles';
import { SQUAD_ROLES, isSquadManager, roleLabel } from '../utils/squadRoles';

/**
 * MemberList - Self-contained component for displaying and managing group members
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 * - onAddMemberClick: Callback to open AddInviteMembers modal
 * - showActions: Boolean to show/hide action buttons (default: true)
 */
const MemberList = ({
  voProvider,
  onAddMemberClick,
  onInvitedClick,
  showActions = true
}) => {
  const { theme } = useTheme();

  // Get current user. Uses AuthContext (backed by secureStorage) rather than the
  // localStorage-based getCurrentUser() helper — that helper is never populated on
  // native (Capacitor Preferences, not the WebView's localStorage, is where native
  // sessions live), which silently broke the "accept invitation" button below.
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ? String(currentUser.id) : null;

  // Local state
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, member: null, action: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Guard: Return loading state if voProvider is not available
  if (!voProvider) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Get data from VO
  const group = voProvider.getGroup();
  const groupId = voProvider.vo.groupId;
  const userRole = group?.userRole;
  // A squad a program runs as a class gets every role; any other squad keeps
  // the admin/member toggle it always had.
  const isClassSquad = Boolean(group?.programId);
  const memberCount = group?.memberCount || members.length;
  const currentUserStatus = group?.memberStatus;

  // Load members from VO or fetch if needed
  const loadMembers = useCallback(async () => {
    setLoading(true);

    // First check if VO has members
    const voMembers = voProvider.getMembers();
    if (voMembers && voMembers.length > 0) {
      setMembers(voMembers);
      setLoading(false);
    } else {
      // Fetch from API
      try {
        const result = await apiCall(`/groups/${groupId}/members`);
        const members = result.data || result;
        setMembers(members);
        voProvider.setMembers(members); // Update VO
      } catch (error) {
        console.error('Error loading members:', error);
        toast.error('Failed to load members');
      }
      setLoading(false);
    }
  }, [groupId, voProvider]);

  // Sort members: admins → active members → invited → deleted
  const sortMembers = useCallback((membersList) => {
    const statusOrder = (m) => {
      if (m.isDeletedUser || m.status === 'deleted') return 3;
      if (m.status === 'invited' || m.status === 'email_invited' || m.status === 'phone_invited' || m.isEmailInvite || m.isPhoneInvite) return 2;
      return 0; // active
    };
    return [...membersList].sort((a, b) => {
      const aOrder = statusOrder(a);
      const bOrder = statusOrder(b);
      if (aOrder !== bOrder) return aOrder - bOrder;

      // Within active: whoever runs the squad first
      if (aOrder === 0) {
        if (isSquadManager(a.role) && !isSquadManager(b.role)) return -1;
        if (!isSquadManager(a.role) && isSquadManager(b.role)) return 1;
      }

      // Then by name
      const aName = a.firstName || a.username || '';
      const bName = b.firstName || b.username || '';
      return aName.localeCompare(bName);
    });
  }, []);

  // Load pending join requests (admin only)
  const loadPendingRequests = useCallback(async () => {
    if (!isSquadManager(userRole)) return;

    setLoadingPendingRequests(true);
    try {
      const result = await apiCall(`/groups/${groupId}/join-requests`);
      const data = result.data || result;
      setPendingRequests(data || []);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
    setLoadingPendingRequests(false);
  }, [groupId, userRole]);

  // Initial load
  useEffect(() => {
    if (groupId) {
      loadMembers();
      loadPendingRequests();
    }
  }, [groupId, loadMembers, loadPendingRequests]);

  // Listen to voProvider state changes for real-time updates
  useEffect(() => {
    const handleStateChange = (changeType, data) => {
      if (changeType === 'members') {
        setMembers(data);
      }
    };

    // Set up listener
    voProvider.setOnStateChange(handleStateChange);

    // Cleanup listener on unmount
    return () => {
      voProvider.setOnStateChange(null);
    };
  }, [voProvider]);

  // Refresh handler
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const result = await apiCall(`/groups/${groupId}/members`);
      const members = result.data || result;
      setMembers(members);
      voProvider.setMembers(members);
    } catch (error) {
      console.error('Error refreshing members:', error);
      toast.error('Failed to refresh members');
    }
    setLoading(false);

    // Also refresh pending requests if admin
    if (isSquadManager(userRole)) {
      loadPendingRequests();
    }
  };

  // Accept invitation handler
  const handleAcceptInvitation = async () => {
    setAcceptingInvitation(true);
    try {
      await apiCall(`/groups/${groupId}/accept-invitation`, { method: 'POST', body: JSON.stringify({}) });
      toast.success('Invitation accepted!');
      handleRefresh();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to accept invitation');
    }
    setAcceptingInvitation(false);
  };

  // Accept join request handler
  const handleAcceptRequest = async (userId) => {
    setProcessingRequest(userId);
    try {
      await apiCall(`/groups/${groupId}/join-requests/${userId}/accept`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast.success('Join request accepted');
      setPendingRequests(prev => prev.filter(r => r.userId !== userId));
      handleRefresh();
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Failed to accept request');
    }
    setProcessingRequest(null);
  };

  // Reject join request handler
  const handleRejectRequest = async (userId) => {
    setProcessingRequest(userId);
    try {
      await apiCall(`/groups/${groupId}/join-requests/${userId}`, { method: 'DELETE' });
      toast.success('Join request rejected');
      setPendingRequests(prev => prev.filter(r => r.userId !== userId));
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
    }
    setProcessingRequest(null);
  };

  // Role change handler
  const handleRoleChange = async (member, newRole) => {
    const memberId = member.userId || member.id;
    try {
      await apiCall(`/groups/${groupId}/members/role`, {
        method: 'PUT',
        body: JSON.stringify({ targetUserId: String(memberId), role: newRole }),
      });
      toast.success(`Role updated to ${newRole}`);
      handleRefresh();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  // Remove member handler
  const handleRemoveMember = async (member) => {
    try {
      if (String(member.id || '').includes(':')) {
        // email_invite:123 or phone_invite:123 — remove from invitations table
        const invitationId = String(member.id).split(':')[1];
        await apiCall(`/groups/${groupId}/invitations/${invitationId}`, { method: 'DELETE' });
        toast.success('Invitation removed');
      } else {
        const memberId = String(member.userId || member.id);
        await apiCall(`/groups/${groupId}/members/${memberId}`, { method: 'DELETE' });
        toast.success('Member removed');
      }
      handleRefresh();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error.message || 'Failed to remove');
    }
  };

  // Confirm modal handlers
  const openRemoveConfirm = (member) => {
    setConfirmModal({ isOpen: true, member, action: 'remove' });
  };

  const openRoleChangeConfirm = (member, newRole) => {
    setConfirmModal({
      isOpen: true,
      member,
      action: newRole === 'admin' ? 'makeAdmin' : 'removeAdmin'
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.member) return;

    setConfirmLoading(true);

    if (confirmModal.action === 'remove') {
      await handleRemoveMember(confirmModal.member);
    } else if (confirmModal.action === 'makeAdmin' || confirmModal.action === 'removeAdmin') {
      const newRole = confirmModal.action === 'makeAdmin' ? 'admin' : 'member';
      await handleRoleChange(confirmModal.member, newRole);
    }

    setConfirmLoading(false);
    setConfirmModal({ isOpen: false, member: null, action: null });
  };


  const getConfirmModalProps = () => {
    const memberName = confirmModal.member?.firstName || confirmModal.member?.username || 'this member';

    if (confirmModal.action === 'remove') {
      const isInvite = confirmModal.member?.isEmailInvite || confirmModal.member?.isPhoneInvite;
      return {
        title: isInvite ? 'Remove Invitation' : 'Remove Member',
        message: isInvite
          ? `Are you sure you want to cancel the invitation for ${memberName}?`
          : `Are you sure you want to remove ${memberName} from the group? This action cannot be undone.`,
        confirmText: 'Remove',
        confirmStyle: 'danger'
      };
    } else if (confirmModal.action === 'makeAdmin') {
      return {
        title: 'Make Admin',
        message: `Are you sure you want to make ${memberName} an admin? They will have full control over this group.`,
        confirmText: 'Make Admin',
        confirmStyle: 'warning'
      };
    } else if (confirmModal.action === 'removeAdmin') {
      return {
        title: 'Remove Admin',
        message: `Are you sure you want to remove admin privileges from ${memberName}?`,
        confirmText: 'Remove Admin',
        confirmStyle: 'primary'
      };
    }
    return {};
  };

  return (
    <div className={`${theme.card} rounded-2xl p-6 border ${theme.border}`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className={`text-lg font-semibold ${theme.text} whitespace-nowrap flex items-center gap-2`}>
          Members ({memberCount})
          {(() => {
            const invitedCount = members.filter(m =>
              !m.isDeletedUser && m.status !== 'deleted' &&
              (m.status === 'invited' || m.status === 'email_invited' || m.status === 'phone_invited' || m.isEmailInvite || m.isPhoneInvite)
            ).length;
            return invitedCount > 0
              ? <span className="text-sm font-normal text-orange-400">· {invitedCount} invited</span>
              : null;
          })()}
        </h3>
        <div className="flex items-center gap-x-2">
          <button
            onClick={handleRefresh}
            className={`p-2 ${theme.cardHover} rounded-lg transition-colors`}
            title="Refresh members list"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${theme.text} ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isSquadManager(userRole) && (
            <button
              onClick={onAddMemberClick}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1 whitespace-nowrap`}
            >
              <UserPlus className="w-4 h-4" />
              <span>Add</span>
            </button>
          )}
        </div>
      </div>

      {/* Pending Join Requests Section (Admin Only) */}
      {isSquadManager(userRole) && pendingRequests.length > 0 && (
        <div className="mb-6">
          <div className={`p-4 rounded-lg border-2 border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800`}>
            <div className="flex items-center gap-x-2 mb-3">
              <Clock className="w-5 h-5 text-orange-600" />
              <h4 className="font-semibold text-orange-800 dark:text-orange-200">
                Pending Join Requests ({pendingRequests.length})
              </h4>
            </div>
            <div className="space-y-2">
              {pendingRequests.map((request) => (
                <div
                  key={request.userId}
                  className={`flex items-start gap-x-3 p-3 ${theme.backgroundSecondary} rounded-lg`}
                >
                  <ProfileAvatar
                    userId={request.userId}
                    profileImage={request.profilePicture}
                    firstName={request.firstName}
                    lastName={request.lastName}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium truncate ${theme.text}`}>
                      {request.firstName} {request.lastName}
                    </p>
                    {request.email && (
                      <p className={`text-sm truncate ${theme.textMuted}`}>
                        {request.email}
                      </p>
                    )}
                    <div className="flex items-center gap-x-2 mt-2">
                      <button
                        onClick={() => handleAcceptRequest(request.userId)}
                        disabled={processingRequest === request.userId}
                        className={`px-3 py-1.5 text-sm rounded-lg ${theme.buttonPrimary} transition-colors flex items-center gap-x-1 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {processingRequest === request.userId ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                        <span>Accept</span>
                      </button>
                      <button
                        onClick={() => handleRejectRequest(request.userId)}
                        disabled={processingRequest === request.userId}
                        className={`px-3 py-1.5 text-sm rounded-lg ${theme.buttonDanger} transition-colors flex items-center gap-x-1 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {processingRequest === request.userId ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Members List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : members.length > 0 ? (
        <div className="space-y-2">
          {sortMembers(members).map((member) => {
            const isDeleted = member.isDeletedUser || member.status === 'deleted';
            const displayName = isDeleted
              ? '[Deleted User]'
              : (member.isInvitation
                  ? (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : (member.email || member.phoneNumber))
                  : (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.username)
                );

            return (
              <div
                key={member.id || member.userId}
                className={`p-3 ${theme.backgroundSecondary} rounded-lg ${
                  member.isInvitation ? 'border-l-4 border-orange-400' : ''
                } ${isDeleted ? 'opacity-60' : ''}`}
              >
                {/* Single row layout: Avatar | Name/Info | Role Badge | Actions */}
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  {isDeleted ? (
                    // Deleted user avatar - gray with "?"
                    <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-lg font-bold text-white">?</span>
                    </div>
                  ) : (
                    <ProfileAvatar
                      userId={member.userId}
                      profileImage={member.profilePicture}
                      firstName={member.firstName}
                      lastName={member.lastName}
                      size="md"
                      className="flex-shrink-0 mt-0.5"
                    />
                  )}

                  {/* Name and Info - responsive column */}
                  <div className="min-w-0 flex-1">
                    <h4 className={`font-semibold ${theme.text} break-words text-sm`}>
                      {displayName}
                    </h4>
                    <p className={`text-xs ${theme.textMuted} break-words`}>
                      {member.isInvitation ? 'Invited' : isDeleted ? 'Account deleted' : 'Joined'} {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : ''}
                    </p>
                  </div>

                  {/* Role Badge */}
                  {(() => {
                    const isInvited = !isDeleted && (member.role === 'invited' || member.status === 'invited' || member.status === 'pending' || member.status === 'email_invited' || member.status === 'phone_invited' || member.isEmailInvite || member.isPhoneInvite);
                    const badgeClass = `px-2 py-1 text-xs rounded-full whitespace-nowrap flex-shrink-0 ${
                      isDeleted
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : isInvited
                        ? 'bg-orange-100 text-orange-800'
                        : isSquadManager(member.role)
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`;
                    // Invited comes before the role: somebody invited as a coach is
                    // not running anything yet, and the badge is how they get reminded.
                    const badgeContent = isDeleted ? 'Deleted'
                      : isInvited ? <span className="flex items-center gap-1">Invited<Bell className="w-3 h-3" /></span>
                      : isSquadManager(member.role) ? <><Crown className="w-3 h-3 inline mr-0.5" />{roleLabel(member.role)}</>
                      : roleLabel(member.role);

                    // For invited members where admin can resend: make badge a clickable link
                    // email_invite:123 or phone_invite:123 → extract numeric id; plain userId → null
                    const invitationId = isInvited && member.id && String(member.id).includes(':')
                      ? String(member.id).split(':')[1]
                      : null;
                    const memberId = member.userId || member.id;
                    return isInvited && isSquadManager(userRole) ? (
                      <button
                        className={`${badgeClass} cursor-pointer hover:bg-orange-200 transition-colors`}
                        onClick={() => onInvitedClick && onInvitedClick(invitationId, invitationId ? null : memberId)}
                        title="Resend invitation"
                      >
                        {badgeContent}
                      </button>
                    ) : (
                      <span className={badgeClass}>{badgeContent}</span>
                    );
                  })()}

                {/* Action Buttons - icon only (hidden for deleted users) */}
                {!isDeleted && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Accept button for current user with pending invitation */}
                    {(member.id || member.userId) === currentUserId &&
                     (currentUserStatus === 'pending' || currentUserStatus === 'invited' || member.status === 'invited') &&
                     !member.isInvitation && (
                      <button
                        onClick={handleAcceptInvitation}
                        disabled={acceptingInvitation}
                        className={`p-2 rounded-lg ${theme.buttonPrimary} transition-colors disabled:opacity-50 flex-shrink-0`}
                        title="Accept invitation"
                      >
                        {acceptingInvitation ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </button>
                    )}

                    {/* Remove button for invited members (admin only) */}
                    {showActions && isSquadManager(userRole) && member.isInvitation && (
                      <button
                        onClick={() => openRemoveConfirm(member)}
                        className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors flex-shrink-0"
                        title="Remove invitation"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    )}

                    {/* Admin actions for full members */}
                    {showActions && isSquadManager(userRole) && !member.isInvitation &&
                     (member.id || member.userId) !== currentUserId && (
                      <>
                        {/* A class squad picks any role; any other squad toggles admin */}
                        {member.role !== 'invited' && member.status !== 'pending' && member.status !== 'invited' && isClassSquad && (
                          <select
                            aria-label={`Role for ${displayName}`}
                            value={member.role}
                            onChange={(e) => handleRoleChange(member, e.target.value)}
                            className={`text-xs rounded-lg border ${theme.border} ${theme.input} px-2 py-1 flex-shrink-0`}
                          >
                            {SQUAD_ROLES.map(role => (
                              <option key={role} value={role}>{roleLabel(role)}</option>
                            ))}
                          </select>
                        )}
                        {member.role !== 'invited' && member.status !== 'pending' && member.status !== 'invited' && !isClassSquad && (
                          <button
                            onClick={() => openRoleChangeConfirm(member, member.role === 'admin' ? 'member' : 'admin')}
                            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                              member.role === 'admin'
                                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                            }`}
                            title={member.role === 'admin' ? 'Remove admin' : 'Make admin'}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                        {/* Remove button */}
                        <button
                          onClick={() => openRemoveConfirm(member)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors flex-shrink-0"
                          title="Remove member"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className={`text-lg font-medium ${theme.text} mb-1`}>No members to show</p>
          <p className={`${theme.textMuted}`}>
            {isSquadManager(userRole) ? 'Start adding members!' : 'Check back later for member updates'}
          </p>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, member: null, action: null })}
        onConfirm={handleConfirmAction}
        loading={confirmLoading}
        {...getConfirmModalProps()}
      />

    </div>
  );
};

export default MemberList;
